import React, { forwardRef } from "react";
import styled from "styled-components";
import { InputPrimitiveProps } from "@xsolla/xui-primitives-core";
import { createFilteredElement } from "./filterDOMProps";

const FilteredInput = createFilteredElement("input");

const StyledInput = styled(FilteredInput)<InputPrimitiveProps>`
  background: transparent;
  border: none;
  outline: none;
  width: 100%;
  height: 100%;
  padding: 0;
  margin: 0;
  color: ${(props) => props.color || "inherit"};
  font-size: ${(props) =>
    typeof props.fontSize === "number"
      ? `${props.fontSize}px`
      : props.fontSize || "inherit"};
  font-family: ${(props) =>
    props.fontFamily ||
    '"Aktiv Grotesk", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'};
  text-align: inherit;

  &::placeholder {
    color: ${(props) =>
      props.placeholderTextColor || "rgba(255, 255, 255, 0.5)"};
  }

  &:disabled {
    cursor: not-allowed;
  }

  /* Override browser autofill background */
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 1000px transparent inset !important;
    -webkit-background-clip: text !important;
    -webkit-text-fill-color: ${(props) => props.color || "inherit"} !important;
  }
`;

export const InputPrimitive = forwardRef<HTMLInputElement, InputPrimitiveProps>(
  (
    {
      value,
      placeholder,
      onChange,
      onChangeText,
      onFocus,
      onBlur,
      onKeyDown,
      disabled,
      secureTextEntry,
      style,
      color,
      fontSize,
      fontFamily,
      placeholderTextColor,
      maxLength,
      name,
      type,
      inputMode,
      autoComplete,
      id,
      "aria-invalid": ariaInvalid,
      "aria-describedby": ariaDescribedBy,
      "aria-labelledby": ariaLabelledBy,
      "aria-label": ariaLabel,
      "aria-disabled": ariaDisabled,
      "data-testid": dataTestId,
      testID,
      ...rest
    },
    ref
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(e);
      }
      if (onChangeText) {
        onChangeText(e.target.value);
      }
    };

    return (
      <StyledInput
        ref={ref}
        id={id}
        value={value}
        name={name}
        placeholder={placeholder}
        onChange={handleChange}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        disabled={disabled}
        type={secureTextEntry ? "password" : type || "text"}
        inputMode={inputMode}
        autoComplete={autoComplete}
        style={style}
        color={color}
        fontSize={fontSize}
        fontFamily={fontFamily}
        placeholderTextColor={placeholderTextColor}
        maxLength={maxLength}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        aria-labelledby={ariaLabelledBy}
        aria-label={ariaLabel}
        aria-disabled={ariaDisabled}
        data-testid={dataTestId || testID}
        {...rest}
      />
    );
  }
);

InputPrimitive.displayName = "InputPrimitive";
